/**
 * Role Service
 *
 * Every role call the frontend makes goes through the Echo aggregator (MS30) proxy - both the
 * self-service read used by the v2 permission catalogue and the role-management CRUD used by
 * the Settings > Role Management screen. The frontend must never call the role-management
 * service directly, so `api-path-rewriter` maps only `/api/aggregator` for these paths.
 *
 * The self-service read is deliberately a different route from `GET /roles/{id}`: the latter is
 * gated behind `RoleRoleGroup.View`, an admin permission only half the Echo roles hold, so
 * ordinary users would get a 403 and never load their own permission grid. The v2 catalogue
 * checks the frontend against the permissions of the single role picked at `/additional-info`,
 * not against the union of every role the user holds, which is what `/users/current/permissions`
 * returns and what the `x-role` header exists to narrow down.
 */

import axiosInstance, { type ApiResponse } from "@/api";
import { isNotFoundError } from "@/api/http-errors";
import type { IRoleEntity, SystemModule, TRoleResponse } from "@/pages/AdditionalInfo/types";
import type { IPermission } from "@/services/staff";
import { Logger } from "@/utils";

const ROLES_URL = "/api/aggregator/roles";
const SYSTEM_MODULES_URL = "/api/aggregator/system-modules";

/** The aggregator caps the page size, so a single large page stands in for "fetch everything". */
const ROLE_LIST_PAGE_SIZE = 1000;

const currentUserRolePermissionsUrl = (roleId: string): string =>
  `/api/aggregator/users/current/roles/${roleId}/permissions`;

export interface ICreateRolePayload {
  name: string;
  precedence?: number;
  roleGroupId?: number;
  systemModuleIds?: number[];
}

export interface IUpdateRolePayload {
  name: string;
  precedence?: number;
  /**
   * Upstream runs UpdateRoleGroupAsync with whatever arrives here, so omitting the field on an
   * edit clears the role's group. Always send the role's current value when updating.
   */
  roleGroupId?: number | null;
}

export interface IRoleSystemModulesPayload {
  roleId: number;
  systemModuleIds: number[];
}

const roleService = {
  /**
   * Get the permissions of one role the signed-in user holds
   *
   * @param roleId Role id, as stored in `useAdditionalInfo.role.value`
   * @returns The role's permissions, or an empty list when the user does not hold that role
   */
  getCurrentUserRolePermissions: async (roleId: string): Promise<IPermission[]> => {
    try {
      const response = await axiosInstance.get<ApiResponse<IPermission[]>>(
        currentUserRolePermissionsUrl(roleId),
      );
      return response.data.data ?? [];
    } catch (error) {
      // A 404 is a definitive "no permissions" answer, not a transport failure.
      if (isNotFoundError(error)) {
        Logger.warn("Selected role is not assigned to the current user", { roleId });
        return [];
      }
      throw error;
    }
  },

  /**
   * Get the permission names of one role the signed-in user holds
   *
   * @param roleId Role id, as stored in `useAdditionalInfo.role.value`
   * @returns Permission names, or an empty list when the role grants none
   */
  getRolePermissionNames: async (roleId: string): Promise<string[]> => {
    const permissions = await roleService.getCurrentUserRolePermissions(roleId);
    return permissions.map((permission) => permission.name).filter(Boolean);
  },

  /**
   * GET /api/aggregator/roles - the whole role list in one page, as the UI has no pagination.
   *
   * @returns The paginated role list envelope, already unwrapped from `{ success, data, meta }`
   */
  getRoleList: async (): Promise<TRoleResponse> => {
    const url = `${ROLES_URL}?pageNumber=1&pageSize=${ROLE_LIST_PAGE_SIZE}`;
    const response = await axiosInstance.get<ApiResponse<TRoleResponse>>(url);
    const roleList = response.data.data;

    // The page size is already the aggregator's maximum, so a second page cannot be asked for
    // here. Roles beyond the first page would simply be missing from every screen, which is
    // worth a loud log rather than a silent truncation.
    if (roleList.totalPages > 1) {
      Logger.warn("Role list is truncated: more roles exist than one page can return", {
        pageSize: ROLE_LIST_PAGE_SIZE,
        totalCount: roleList.totalCount,
        totalPages: roleList.totalPages,
      });
    }

    return roleList;
  },

  /**
   * GET /api/aggregator/roles/{id} - role with its granted permissions and assigned modules.
   *
   * @param roleId Role id
   * @returns The role, with `systemModules -> functions -> permissions` eager-loaded
   */
  getRoleDetail: async (roleId: number): Promise<IRoleEntity> => {
    const response = await axiosInstance.get<ApiResponse<IRoleEntity>>(`${ROLES_URL}/${roleId}`);
    return response.data.data;
  },

  /**
   * POST /api/aggregator/roles - the aggregator creates the role and assigns its system
   * modules in a single call.
   *
   * @param payload Role name, optional precedence / group, and the modules to assign
   * @returns The created role
   */
  createNewRole: async (payload: ICreateRolePayload): Promise<IRoleEntity> => {
    const response = await axiosInstance.post<ApiResponse<IRoleEntity>>(ROLES_URL, payload);
    return response.data.data;
  },

  /**
   * PUT /api/aggregator/roles/{id} - rename a role / change its precedence or group.
   *
   * @param roleId Role id
   * @param payload The role's new name, precedence and group
   * @returns The updated role
   */
  updateRole: async (roleId: number, payload: IUpdateRolePayload): Promise<IRoleEntity> => {
    const response = await axiosInstance.put<ApiResponse<IRoleEntity>>(
      `${ROLES_URL}/${roleId}`,
      payload,
    );
    return response.data.data;
  },

  /**
   * PUT /api/aggregator/roles/system-modules - replace the module assignment of one or more
   * roles. The aggregator expects the list wrapped in a `roles` object.
   *
   * @param roles One entry per role, each carrying the full module list that role should have
   * @returns The modules the aggregator ended up assigning
   */
  updateSystemModulesToRole: async (
    roles: IRoleSystemModulesPayload[],
  ): Promise<SystemModule[]> => {
    const response = await axiosInstance.put<ApiResponse<SystemModule[]>>(
      `${ROLES_URL}/system-modules`,
      { roles },
    );
    return response.data.data;
  },

  /**
   * PUT /api/aggregator/roles/{id}/permissions - replace the permission set of a role.
   *
   * @param roleId Role id
   * @param permissionIds The complete permission set the role should end up with
   * @returns The updated role
   */
  updatePermissions: async (roleId: number, permissionIds: number[]): Promise<IRoleEntity> => {
    const response = await axiosInstance.put<ApiResponse<IRoleEntity>>(
      `${ROLES_URL}/${roleId}/permissions`,
      { permissionIds },
    );
    return response.data.data;
  },

  /**
   * GET /api/aggregator/system-modules - flat array; the aggregator already collapses pagination.
   *
   * @returns Every system module, without its functions
   */
  getSystemModulesList: async (): Promise<SystemModule[]> => {
    const response = await axiosInstance.get<ApiResponse<SystemModule[]>>(SYSTEM_MODULES_URL);
    return response.data.data;
  },

  // GET /api/aggregator/system-modules/{id}/functions is deliberately not wrapped. Its upstream
  // query ignores the module id in the path and returns the functions of every module, and the
  // permission grid does not need it: GET /roles/{id} already nests functions under each module.
};

export const { getCurrentUserRolePermissions, getRolePermissionNames } = roleService;

export default roleService;
